<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

import { computed } from 'vue'

import { Button as KButton } from '@progress/kendo-vue-buttons'

import { useLogger } from 'vue-logger-plugin'

import type { IEventOrderResponse, IEventSubmittedOrder } from '@/types/EventTypes'

import { formatDateWithShortMonthAndTime } from "@/api/Common.ts";

import AoePartyOrderSummaryGrid from '@/components/Controls/AoePartyOrderSummaryGrid.vue'

const logger = useLogger()

const emit = defineEmits(["addGuestButtonClick", "update-cart"]);

// Submitted orders are fetched once by the parent (AoePartyOrderEntry) - it
// needs the same list to build sidebar tabs for guests who already submitted
// their order, so it's passed down here rather than fetched a second time.
const props = defineProps<{
  eventorder: IEventOrderResponse
  hostonly: boolean
  submittedorders?: IEventSubmittedOrder[]
  loadingsubmittedorders?: boolean
}>()

function addGuestButtonClickHandler(event: MouseEvent) {
  logger.debug('addGuestButtonClickHandler')
  emit('addGuestButtonClick', event)
}

function updateCartHandler(event: MouseEvent) {
  logger.debug('updateCartHandler AoeTabbedProductEntry', event)
  emit('update-cart', event)
}

// Submitted orders are removed from carts once the order is placed, so both
// counts below need to add them back in to include guests who already
// checked out.
const numberOfGuests = computed(() => {
  return (props.eventorder.item.carts.length - 2) + (props.submittedorders?.length ?? 0)
})

const numberOfGuestOrders = computed(() => {
  const cartOrders = props.eventorder.item.carts.filter(cart => cart.isHostCart == false && cart.isConsultantCart == false && cart.total > 0).length
  return cartOrders + (props.submittedorders?.length ?? 0)
})

</script>

<template>
  <div v-if="props.eventorder != null" class="aoe-party-order-summary-control">
    <div v-if="!props.hostonly">
      <div class="row mt-3 party-order-entry-section-header-row">
        <div class="col-12 party-order-entry-section-header-div party-order-summary-section-header">
          {{ props.eventorder.item.event.eventName }}
        </div>
      </div>
      <div class="row mt-3 event-id-row">
        <div class="col-6 col-md-4 event-id-label-div">
          {{ t('partyOrderEntry.EventId') }}:
        </div>
        <div class="col-6 col-md-4 event-id-value-div">
          {{ props.eventorder.item.event.eventId }}
        </div>
      </div>
      <div class="row mt-3 event-number-guests-row">
        <div class="col-6 col-md-4 event-number-guests-label-div">
          {{ t('partyOrderEntry.NumberOfGuests') }}:
        </div>
        <div class="col-6 col-md-4 event-number-guests-value-div">
          {{ numberOfGuests }}
        </div>
      </div>
      <div class="row mt-3 event-event-date-row">
        <div class="col-6 col-md-4 event-event-date-label-div">
          {{ t('partyOrderEntry.EventDate') }}:
        </div>
        <div class="col-6 col-md-4 event-event-date-value-div">
          {{ formatDateWithShortMonthAndTime(props.eventorder.item.event.eventDate.toString()) }}
        </div>
      </div>
      <div v-if="props.eventorder.item.event.host" class="row mt-3 event-host-row">
        <div class="col-6 col-md-4 event-host-label-div">
          {{ t('partyOrderEntry.Host') }}:
        </div>
        <div class="col-6 col-md-4 event-host-value-div">
          {{ props.eventorder.item.event.host.firstName ?? '' }} {{ props.eventorder.item.event.host.lastName ?? '' }}
        </div>
      </div>
      <div v-if="props.eventorder.item.event.host?.phoneNumber" class="row mt-1 event-host-phone-row">
        <div class="col-6 col-md-4">
        </div>
        <div class="col-6 col-md-4 event-host-phone-value-div">
          {{ props.eventorder.item.event.host.phoneNumber.formattedNumber ?? props.eventorder.item.event.host.phoneNumber.number }}
        </div>
      </div>
      <div v-if="props.eventorder.item.event.host?.email" class="row mt-1 event-host-email-row">
        <div class="col-6 col-md-4">
        </div>
        <div class="col-6 col-md-4 event-host-email-value-div">
          {{ props.eventorder.item.event.host.email }}
        </div>
      </div>
      <div class="row mt-3 event-number-guest-orders-row">
        <div class="col-6 col-md-4 event-number-guest-orders-label-div">
          {{ t('partyOrderEntry.NumberOfGuestOrders') }}:
        </div>
        <div class="col-6 col-md-4 event-number-guest-orders-value-div">
          {{ numberOfGuestOrders }}
        </div>
      </div>
      <div class="row mt-3 add-guest-button-row">
        <div class="col-12 add-guest-button-div">
          <KButton
            :rounded="'large'"
            class="baseBtn primaryBtn update-cart-button"
            @click="addGuestButtonClickHandler($event)"
            >{{ t('partyOrderEntry.AddGuestButton') }}
          </KButton>
        </div>
      </div>
      <div class="row mt-3 party-order-entry-section-header-row">
        <div class="col-12 party-order-entry-section-header-div party-order-summary-guest-section-header">
          {{ t('partyOrderEntry.PartyOrderSummaryGuestOrdersSectionHeader') }}
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-12">
          <AoePartyOrderSummaryGrid
            :griddataitems="props.eventorder.item.carts.filter(cart => cart.isHostCart == false && cart.isConsultantCart == false)"
            :disableactivate="false"
            :submittedorders="props.submittedorders ?? []"
            :loadingsubmittedorders="props.loadingsubmittedorders ?? false"
            @update-cart="updateCartHandler" />
        </div>
      </div>
    </div>
    <div class="row mt-3 party-order-entry-section-header-row">
      <div class="col-12 party-order-entry-section-header-div party-order-summary-host-section-header">
        {{ t('partyOrderEntry.PartyOrderSummaryHostOrdersSectionHeader') }}
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12">
        <AoePartyOrderSummaryGrid
          :griddataitems="props.eventorder.item.carts.filter(cart => cart.isHostCart == true)"
          :disableactivate="true" />
      </div>
    </div>
  </div>
</template>

<style lang="scss">

.add-guest-button-div
{
  text-align: right;
}

</style>
